import Vue from "vue";
import { camelCase } from "lodash";
import AppMixin from "~/Scripts/config/app";
import store from "~/Scripts/store";

/* eslint-disable class-methods-use-this */

/**
 * A wrapper for creating a Vue instance,
 * intended for use as a multi page site, such as SpiritWeb
 */
export default class VueApp {
  /**
   * Construct the Vue app
   * @param {*} options Vue options
   */
  constructor ( options ) {
    this.requireCriticalStyles( options.name );

    const mergedOptions = {
      ...options,
      el: "#app", // By convention we know we're going to look for #app
      store: this.replaceState(),
      mixins: this.replaceMixins( options.mixins ),
    };

    new Vue( mergedOptions );
  }

  /**
   * Require critical styles if not in production,
   * as the template will only inline them in production.
   */
  requireCriticalStyles ( name ) {
    const cameledName = camelCase( name );

    /* eslint-disable global-require */
    if ( process.env.NODE_ENV !== "production" ) {
      // eslint-disable-next-line import/no-dynamic-require
      require( `../${cameledName}.critical` );
    }
    /* eslint-enable global-require */
  }

  /**
   * Attempt to find and inject server provided Vuex state.
   * @param {*} store The Vuex store.
   */
  replaceState () {
    const { state } = store;

    // Only replace the initial state if something is set
    /* eslint-disable no-underscore-dangle */
    const windowState = window.__INITIAL_STATE__;
    if ( windowState && Object.keys( windowState ).length !== 0 ) {
      store.replaceState( { ...state, ...windowState } );
    }
    /* eslint-enable no-underscore-dangle */

    return store;
  }

  /**
   * Include the provided mixins, as well as our global data
   * and app data mixins.
   * @param {*} mixins The provided Vue mixins
   */
  replaceMixins ( mixins ) {
    const result = [];

    // Check if we have an app mixin (to provide common functionality)
    if ( AppMixin ) {
      result.push( AppMixin );
    }

    // Check if we had any page specific mixins
    if ( mixins ) {
      result.push( ...mixins );
    }

    // Create our server data mixin and append that
    const dataMixin = this.createServerDataMixin();
    result.push( dataMixin );

    return result;
  }

  /**
   * Create a mixin containing any data provided from the server.
   */
  createServerDataMixin () {
    let data = {};

    // Only replace the initial data if something is set
    /* eslint-disable no-underscore-dangle */
    const windowData = window.__INITIAL_DATA__;
    if ( windowData && Object.keys( windowData ).length !== 0 ) {
      data = windowData;
    }
    /* eslint-enable no-underscore-dangle */

    return {
      data: () => data,
    };
  }
}
